// Pure, dependency-free helpers for periodic account review ID validation.
// Manila (Asia/Manila, UTC+8, no DST) local-date semantics apply:
// an ID is "future-dated" only when its validity is strictly after today;
// today or any earlier date counts as expired.

const STRICT_DATE = /^\d{4}-\d{2}-\d{2}$/;

// NOTE: this is intentionally a stricter, dependency-free variant of
// logic/paths.ts `hasValue`. It trims and case-insensitively rejects "N/A"
// because ID fields (idType/idNumber/idValidity) arrive as raw strings where
// " n/a " must read as empty. paths.ts keeps a looser version for general
// draft/object checks; the two are deliberately not shared.
export function hasValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed !== "" && trimmed.toUpperCase() !== "N/A";
  }
  return true;
}

// Returns today's date in Asia/Manila as a "YYYY-MM-DD" string.
// `now` is injectable so callers (and tests) stay deterministic.
export function getManilaToday(now: Date = new Date()): string {
  return now.toLocaleDateString("en-CA", { timeZone: "Asia/Manila" });
}

// True only for a strict, real "YYYY-MM-DD" calendar date.
// Rejects loose formats and overflow dates such as 2026-02-30 or 2026-13-01.
export function isStrictDate(value: unknown): value is string {
  if (typeof value !== "string" || !STRICT_DATE.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

// Strict YYYY-MM-DD and strictly after today (Manila). Today/earlier = expired.
export function isFutureManilaDate(
  value: unknown,
  today: string = getManilaToday()
): boolean {
  if (!isStrictDate(value)) return false;
  return value > today;
}

// Returns an error string for the ID expiration field, or "" when valid.
// Missing legacy values default to an expiring ID.
export function validateIdExpiration(
  idValidity: unknown,
  hasExpiration: boolean,
  today: string = getManilaToday()
): string {
  if (!hasExpiration) return "";
  if (!hasValue(idValidity)) return "ID expiration date is required.";
  if (!isStrictDate(idValidity)) {
    return "ID expiration date must be in YYYY-MM-DD format.";
  }
  if (!isFutureManilaDate(idValidity, today)) {
    return "ID has expired. Please provide an ID with a future expiration date.";
  }
  return "";
}

function normalize(value: unknown): string {
  return hasValue(value) ? String(value).trim() : "";
}

export interface IdValidityFields {
  idType?: unknown;
  idNumber?: unknown;
  idValidity?: unknown;
  hasExpiration?: boolean;
}

// Retained for legacy callers that still distinguish the "None" ID sentinel.
export function idIsSelected(idType: unknown): boolean {
  return hasValue(idType) && String(idType).trim().toLowerCase() !== "none";
}

export interface FreshIdImagesArgs {
  original?: IdValidityFields;
  draft?: IdValidityFields;
  hasExistingFront?: boolean;
  hasExistingBack?: boolean;
}

// Human-readable reason fresh front+back ID photos are required, or "" when
// they are not. `original`/`draft` are the `ids` objects; `hasExistingFront`/
// `hasExistingBack` indicate whether the approved record already carries those
// image metadata. This is the single source of truth shared with
// `requiresFreshIdImages`, so the upload gate and the explanatory copy never
// drift. Expiration state and date are independently reviewable fields, so only
// missing evidence or changed ID identity requires replacement photos.
export function freshIdReason({
  original = {},
  draft = {},
  hasExistingFront = false,
  hasExistingBack = false,
}: FreshIdImagesArgs = {}): string {
  if (!hasExistingFront || !hasExistingBack) {
    return "We don't have photos of your ID on file yet.";
  }
  if (
    normalize(draft.idType) !== normalize(original.idType) ||
    normalize(draft.idNumber) !== normalize(original.idNumber)
  ) {
    return "You're changing your ID details.";
  }
  return "";
}

// Decides whether fresh front AND back ID uploads must be required.
export function requiresFreshIdImages(args: FreshIdImagesArgs = {}): boolean {
  return freshIdReason(args) !== "";
}
